import classnames from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button, Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, TabContent, TabPane, UncontrolledTooltip } from 'reactstrap'
import auth from '../../api/auth/hooks'
import flags from '../../api/flags/hooks'
import { invalidateQueries } from '../../api/helpers'
import notifications from '../../api/notifications/hooks'
import { isNotificationAuthorization, notificationCodeConfig } from '../../api/notifications/types'
import { DETAIL_SEARCH_TYPE, DOCUMENT_TYPE, MODAL_TYPES, PROPERTY_STATUS } from '../../common/constants'
import { showModal } from '../../store/modal'
import { setDetailSearchCriteria } from '../../store/search/actions'
import { resetTableCheckbox } from '../../store/table'
import { DataTable, useDataTable, useTableState } from '../TanstackTable'
import useActivityReminderToasts from './hooks/useActivityReminderToasts'
import { useTableColumns } from './hooks/useTableColumns'
import { showDismissUndoToast } from './utils'

const NotificationDropdown = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [selectedTab, setSelectedTab] = useState<string>('authorization')
    const [limit, setLimit] = useState<number>(10)

    const notificationCode = notificationCodeConfig[selectedTab]

    const getNotificationConfig = {
        params: {
            'filter[notification.code]': notificationCode,
            'filter[status]': ['unread', 'read'],
            limit: limit,
        },
        isEnabled: {
            enabled: isOpen && !!notificationCode,
        },
    }

    const { data: notifications_system } = flags.GetFeatureFlags.useQuery({
        select: (data) => data.notifications_system,
    })

    const { data: currentAgentId } = auth.CurrentAgent.useQuery({ select: (data) => data.id })

    const { data: notificationData, isPending } = notifications.GetNotificationDeliveries.useQuery(
        getNotificationConfig.params,
        getNotificationConfig.isEnabled
    )

    const { data: activeTabsData } = notifications.GetNotificationActiveTabs.useQuery({
        enabled: isOpen && !!notifications_system,
    })

    const { data: unreadCountData } = notifications.GetTotalUnreadCount.useQuery({
        refetchInterval: 60000, // Refetch every 1 minute
        enabled: !!notifications_system,
    })

    const { data: preferencesResponse } = notifications.GetAgentPreferences.useQuery('', {
        enabled: isOpen && selectedTab === 'documents',
    })

    const { mutate: dismissNotifications } = notifications.DismissNotifications.useMutation()
    const { mutate: restoreNotification } = notifications.RestoreNotifications.useMutation()

    const columns = useTableColumns(selectedTab)
    const tableState = useTableState()
    const tableData = useMemo(() => notificationData?.data?.items ?? [], [notificationData?.data?.items])

    const { table } = useDataTable({
        ...tableState,
        columns,
        data: tableData,
    })

    useActivityReminderToasts(!!notifications_system)

    const totalUnreadCount = unreadCountData?.data?.total ?? 0
    const hasTotalUnreadNotifications = totalUnreadCount > 0

    const hasForAuthorizationProperty =
        notificationData?.data?.items?.length &&
        notificationData?.data.items?.some(
            (item) => isNotificationAuthorization(item) && item.property?.propertyStatusId === PROPERTY_STATUS.FOR_AUTHORIZATION
        )

    const isTabEnabled = useCallback(
        (tabId: string) => {
            const codes = notificationCodeConfig[tabId]
            const activeNotifications = activeTabsData?.data?.notifications ?? []

            if (Array.isArray(codes)) return codes.some((c) => activeNotifications.some((n) => n.code === c))

            return activeNotifications.some((n) => n.code === codes)
        },
        [activeTabsData?.data?.notifications]
    )

    const tabConfig = useMemo(
        () => [
            ...(isTabEnabled('authorization')
                ? [
                      {
                          id: 'authorization',
                          label: t('notification.tab.authorization'),
                          message: hasForAuthorizationProperty ? t('notification.message.authorization') : '',
                      },
                  ]
                : []),
            ...(isTabEnabled('documents')
                ? [{ id: 'documents', label: t('app.common.documents'), message: t('notification.message.documents') }]
                : []),
            ...(isTabEnabled('activities')
                ? [{ id: 'activities', label: t('app.common.activities'), message: t('notification.message.activities') }]
                : []),
            ...(isTabEnabled('offers') ? [{ id: 'offers', label: t('notification.tab.offers'), message: t('notification.message.offers') }] : []),
            ...(isTabEnabled('other') ? [{ id: 'other', label: t('notification.tab.other'), message: t('notification.message.other') }] : []),
        ],
        [t, isTabEnabled, hasForAuthorizationProperty]
    )

    const activeTab = tabConfig.find(({ id }) => id === selectedTab)?.id ?? tabConfig[0]?.id ?? 'authorization'

    const maxNotifyDaysBefore = useMemo(() => {
        const prefs = preferencesResponse?.data?.preferences ?? []
        const days = prefs
            .filter((p) => p.notificationCode.includes('document'))
            .flatMap((p) => p.channels.map((c) => c.notifyDaysBefore))
            .filter((d): d is number => d !== null)

        return days.length ? Math.max(...days) : 30
    }, [preferencesResponse])

    useEffect(() => {
        if (isOpen) {
            invalidateQueries(['notifications', 'deliveries', 'unread-count'])
        } else {
            dispatch(resetTableCheckbox(true))
        }
    }, [isOpen, dispatch])

    const toggleNotificationDropdown = () => setIsOpen(!isOpen)

    const toggleTab = (tab: string) => {
        if (selectedTab !== tab) {
            setSelectedTab(tab)
        }
    }

    const getTabUnreadCount = (tab: string) => {
        const codes = notificationCodeConfig[tab]

        if (!unreadCountData?.data?.byCodes) return 0

        if (Array.isArray(codes)) {
            return codes.reduce((sum, code) => sum + (unreadCountData.data.byCodes[code] ?? 0), 0)
        }

        return unreadCountData.data.byCodes[codes] ?? 0
    }

    const handleOpenAll = () => {
        switch (activeTab) {
            case 'authorization':
                dispatch(setDetailSearchCriteria(DETAIL_SEARCH_TYPE.PROPERTY, { propertyStatus: [PROPERTY_STATUS.FOR_AUTHORIZATION.toString()] }))
                history.push('/properties')
                break
            case 'documents':
                dispatch(
                    setDetailSearchCriteria(DETAIL_SEARCH_TYPE.DOCUMENT, {
                        agent: currentAgentId,
                        // TODO: documentTypeId form field is a single-select, but this filter needs to match either type —
                        // won't render as selected in the modal until the field supports multiple values.
                        documentTypeId: [DOCUMENT_TYPE.MEDIATION_CONTRACT, DOCUMENT_TYPE.RENTAL_CONTRACT],
                        documentValidityDateFrom: dayjs().format('YYYY-MM-DD'),
                        documentValidityDateTo: dayjs().add(maxNotifyDaysBefore, 'day').format('YYYY-MM-DD'),
                    })
                )
                history.push('/documents')
                break
            case 'activities':
                history.push('/activities')
                break
        }

        setIsOpen(false)
    }

    const handleReadAll = () => {
        const payload = {
            modalType: MODAL_TYPES.READ_ALL_MODAL,
            modalProps: { activeTab },
        }

        dispatch(showModal(payload))
    }

    const handleDismissNotifications = () => {
        const codes = notificationCodeConfig[activeTab]
        const notificationCodes = Array.isArray(codes) ? codes : [codes]

        dismissNotifications(
            { all: true, notificationCodes },
            {
                onSuccess: (data) => {
                    const deliveryIds = data?.data?.updatedIds
                    if (deliveryIds?.length) showDismissUndoToast(deliveryIds, restoreNotification)
                },
            }
        )
    }

    return (
        notifications_system && (
            <Dropdown isOpen={isOpen} toggle={toggleNotificationDropdown} className="header-item position-relative ms-1">
                <DropdownToggle
                    type="button"
                    tag="button"
                    className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
                    data-testid="notification-bell"
                >
                    <i className="mdi mdi-bell-outline fs-20"></i>
                    {hasTotalUnreadNotifications && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-success">
                            {totalUnreadCount}
                        </span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end notification-menu p-0" style={{ width: '730px', maxWidth: '100vw' }}>
                    <div className="dropdown-head bg-notification-gradient rounded-top p-3">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h6 className="m-0 fs-16 fw-semibold text-white d-flex align-items-center">
                                    <i className="mdi mdi-bell me-2"></i>
                                    {t('app.common.notifications')}
                                </h6>
                            </div>
                        </div>
                    </div>

                    <div className="px-3 pt-3">
                        <Nav tabs className="nav-tabs-custom nav-success">
                            {tabConfig.map(({ id, label }) => (
                                <NavItem key={id}>
                                    <NavLink
                                        style={{ cursor: 'pointer' }}
                                        className={classnames({ active: activeTab === id })}
                                        onClick={() => toggleTab(id)}
                                    >
                                        {label} {getTabUnreadCount(id) > 0 ? `(${getTabUnreadCount(id)})` : ''}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>
                    </div>

                    <TabContent activeTab={activeTab} className="text-muted">
                        {tabConfig.map(({ id, message }) => (
                            <TabPane key={id} tabId={id} className="p-3">
                                <div className="text-muted">
                                    <p className="mb-3">{message}</p>
                                    {activeTab === id && (
                                        <div style={{ overflowX: 'auto' }}>
                                            <DataTable
                                                table={table}
                                                isLoading={isPending}
                                                theadClass="dmn-table-header"
                                                noResultMessage={t('notification.noResults')}
                                            />
                                        </div>
                                    )}
                                </div>
                            </TabPane>
                        ))}
                    </TabContent>

                    <div className="p-3 border-top d-flex justify-content-between">
                        {limit < (notificationData?.data?.totalCount ?? 0) && (
                            <Button color="link" onClick={() => setLimit((prev) => prev + 10)}>
                                {t('table.showMore')}
                            </Button>
                        )}
                        <div className="d-flex gap-2 ms-auto">
                            {activeTab !== 'other' && activeTab !== 'offers' && (
                                <Button
                                    color="warning"
                                    id="open-all"
                                    className="btn-label"
                                    onClick={handleOpenAll}
                                    disabled={!notificationData?.data?.items?.length}
                                >
                                    <i className="mdi mdi-open-in-new me-1 align-bottom label-icon fs-16 me-2"></i>
                                    {t('button.openAll')}
                                    {activeTab === 'documents' && (
                                        <UncontrolledTooltip placement="top" target="open-all">
                                            {t('notification.tooltip.openAll', { maxNotifyDaysBefore })}
                                        </UncontrolledTooltip>
                                    )}
                                </Button>
                            )}
                            <Button color="info" className="btn-label" onClick={handleReadAll} disabled={getTabUnreadCount(activeTab) === 0}>
                                <i className="mdi mdi-email-open-multiple-outline align-bottom label-icon fs-16 me-2"></i>
                                {t('button.readAll')}
                            </Button>
                            <Button
                                color="danger"
                                className="btn-label"
                                onClick={handleDismissNotifications}
                                disabled={!notificationData?.data?.items?.length}
                            >
                                <i className="mdi mdi-trash-can-outline align-bottom label-icon fs-16 me-2"></i>
                                {t('button.deleteAll')}
                            </Button>
                        </div>
                    </div>
                </DropdownMenu>
            </Dropdown>
        )
    )
}

export default NotificationDropdown
